// Ch78 · the three cards under the inbox: where they come from, when they need
// to move, what they want.
//
// Each one's (i) names its own DENOMINATOR, because that is where a card like
// this lies. "Real %" is measured over the rows the scorer has actually looked
// at, with the unscored count beside it; a source with nothing scored draws "—"
// rather than 0%. Every "not stated" is on the card, not hidden behind the bars.

import React from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InfoDot from "./InfoDot";
import type { Bucket, SourcesModel, WantsModel } from "../../lib/leadsInsights";

export interface LeadsInsightCardsProps {
  sources: SourcesModel;
  timing: Bucket[];
  wants: WantsModel;
  /** The window `sources` and `wants` were counted over. Timing is all-time. */
  rangeDays: number;
  /** How many people the timing chart is drawn from — the calendar's population. */
  timingTotal: number;
}

const SOURCES_INFO =
  "Which channel produces people who are real and who convert. “Real %” is measured over the leads the " +
  "scorer has actually LOOKED at, not over all of them — a source with four unscored leads and one real " +
  "one is not 20% real, it is barely scored, and the unscored count is shown so you can tell the " +
  "difference. A source with nothing scored draws “—”. Counted over the range in the page header.";

const TIMING_INFO =
  "The same people as the calendar above, as a distribution — everyone on this page, not just the " +
  "range in the header, so a lead moving in eight months is still visible. “Past due” means their day " +
  "came and nobody was placed; “No date” means they gave words, not a date, and nothing was guessed.";

const WANTS_INFO =
  "What to have ready. Budget bands count STATED budgets only and the not-stated figure sits beside " +
  "them; areas count every area a person named, so someone who named three appears in three; pets are " +
  "read from the form's own field — a value we do not recognise as a “no” is a yes, an empty field is " +
  "not stated, and nothing is inferred from notes. Counted over the range in the page header.";

function BarRow({ label, count, max, tone }: { label: string; count: number; max: number; tone?: "warn" | "dim" }) {
  const theme = useTheme();
  const color =
    tone === "warn" ? theme.palette.warning.main : tone === "dim" ? theme.palette.text.disabled : theme.palette.primary.main;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "112px 1fr 32px", gap: 1, alignItems: "center" }}>
      <Typography variant="caption" noWrap>{label}</Typography>
      <Box sx={{ height: 12, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.08), overflow: "hidden" }}>
        <Box sx={{ width: max > 0 ? `${Math.max(count > 0 ? 3 : 0, (100 * count) / max)}%` : 0, height: "100%", bgcolor: color }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>{count}</Typography>
    </Box>
  );
}

function CardShell({ title, info, testId, children }: { title: string; info: string; testId: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }} data-testid={testId}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
        <InfoDot text={info} testId={`${testId}-info`} />
      </Stack>
      {children}
    </Paper>
  );
}

export default function LeadsInsightCards({ sources, timing, wants, rangeDays, timingTotal }: LeadsInsightCardsProps) {
  const timingMax = Math.max(1, ...timing.map((b) => b.count));
  const budgetMax = Math.max(1, ...wants.budgets.map((b) => b.count));
  const areaMax = Math.max(1, ...wants.neighborhoods.map((b) => b.count));

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardShell title="Where they come from" info={SOURCES_INFO} testId="insight-sources">
          {sources.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="sources-empty">
              Nobody arrived in the last {rangeDays} days.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Leads</TableCell>
                  <TableCell align="right">Real</TableCell>
                  <TableCell align="right">Clients</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.rows.map((r) => (
                  <TableRow key={r.key} data-testid={`source-row-${r.key}`}>
                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>{r.label}</Typography>
                    </TableCell>
                    <TableCell align="right"><Typography variant="caption">{r.leads}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {r.realPct === null ? "—" : `${r.realPct}%`}
                        {r.scored < r.leads && (
                          <Typography component="span" variant="caption" color="text.disabled">
                            {` (${r.leads - r.scored} unscored)`}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="right"><Typography variant="caption" fontWeight={700}>{r.clients}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {sources.topCampaign && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }} data-testid="sources-campaign">
              Best named campaign: <b>{sources.topCampaign.label}</b> · {sources.topCampaign.leads} leads ·{" "}
              {sources.topCampaign.real} real · {sources.topCampaign.clients} clients
            </Typography>
          )}
          {sources.unattributed > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }} data-testid="sources-unattributed">
              {sources.unattributed} with no source recorded — counted, not dropped.
            </Typography>
          )}
        </CardShell>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardShell title="When they need to move" info={TIMING_INFO} testId="insight-timing">
          <Stack spacing={0.75}>
            {timing.map((b) => (
              <BarRow
                key={b.key}
                label={b.label}
                count={b.count}
                max={timingMax}
                tone={b.key === "past" ? "warn" : b.key === "none" ? "dim" : undefined}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }} data-testid="timing-total">
            {timingTotal} {timingTotal === 1 ? "person" : "people"} on this page.
          </Typography>
        </CardShell>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardShell title="What they want" info={WANTS_INFO} testId="insight-wants">
          <Stack spacing={0.75}>
            {wants.budgets.map((b) => (
              <BarRow key={b.key} label={b.label} count={b.count} max={budgetMax} />
            ))}
          </Stack>
          {wants.budgetNotStated > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }} data-testid="wants-budget-missing">
              {wants.budgetNotStated} stated no budget.
            </Typography>
          )}

          {wants.neighborhoods.length > 0 && (
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {wants.neighborhoods.map((b) => (
                <BarRow key={b.key} label={b.label} count={b.count} max={areaMax} />
              ))}
            </Stack>
          )}
          {wants.areaNotStated > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }} data-testid="wants-area-missing">
              {wants.areaNotStated} named no area.
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }} data-testid="wants-summary">
            Pets: {wants.pets.yes} with · {wants.pets.no} without · {wants.pets.unknown} not stated
            {wants.bedrooms.length > 0 && (
              <>
                {" · "}Most asked: <b>{wants.bedrooms[0].label}</b> ({wants.bedrooms[0].count})
              </>
            )}
          </Typography>
        </CardShell>
      </Box>
    </Stack>
  );
}
